import { getBlogNewsPostsForList, getItems } from '@/services';
import {
  CONTENT_CATEGORY_ID,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  type ContentCategoryId,
} from '@/constants';
import type { PostCardListItem, PostSummary } from '@/utils/post-summary';

const AUTHOR_POSTS_PER_PAGE = 6;

/**
 * 저자 캐시의 PostSummary 를 목록 카드 섹션(news Section.Main)의 계약(PostCardListItem)으로
 * 변환한다 — 뉴스 저자 목록 페이지 전용(UI 무변경 어댑터. 블로그 저자 페이지는
 * BlogPostGrid 로 PostSummary 를 그대로 쓴다).
 *
 * ⚠️ 계약: **로케일 필터된 입력에만 안전하다.** PostSummary 의 category 는 포스트
 * 자신의 언어로 이미 결정된 단일 값이라 ko/en 필드 양쪽에 같은 값을 넣는다 —
 * 저자 페이지는 자기 locale 의 posts 만 넘기므로 항상 올바른 언어가 보이지만,
 * 로케일이 섞인 목록에 재사용하면 반대 언어 필드에 틀린 라벨이 들어간다.
 * 이미지는 캐시 생성 시점에 heroImage→featuredImage 우선순위가 이미 적용된 단일 값이다.
 */
export const toAuthorPageListItem = (post: PostSummary): PostCardListItem => ({
  id: post.id,
  title: post.title,
  slug: post.slug,
  featuredImage: post.image,
  heroImage: post.image,
  categoryKo: [post.category],
  categoryEn: [post.category],
  date: post.createdAt,
});

export async function generateAuthorStaticParams(
  categoryId: ContentCategoryId,
) {
  const [authors, allPosts] = await Promise.all([
    getItems(CONTENT_CATEGORY_ID.authors),
    getBlogNewsPostsForList(categoryId),
  ]);

  const staticPages: {
    locale: SupportedLanguage;
    author: string;
    page: string;
  }[] = [];

  for (const author of authors) {
    if (!author.slug) continue;

    for (const locale of SUPPORTED_LANGUAGES) {
      const filteredPosts = allPosts
        .filter(post => post.lang?.includes(locale))
        .filter(post => post.authorId === author.slug);

      if (filteredPosts.length === 0) continue;

      const endPage = Math.ceil(filteredPosts.length / AUTHOR_POSTS_PER_PAGE);
      for (let page = 1; page <= endPage; page++) {
        staticPages.push({
          locale,
          author: author.slug,
          page: page.toString(),
        });
      }
    }
  }

  return staticPages;
}
